import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useOrganization } from '../../context/OrgContext';
import { LuCheckCircle, LuHardDrive, LuPackage } from "react-icons/lu";
import { collection, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebaseConfig'; // Adjust the import path to your Firebase config
import * as LucideIcons from "react-icons/lu";

const FacultyDashboard = () => {
  const { institution } = useOrganization();
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  // User's accessible modules data
  const [userModules, setUserModules] = useState([]);
  const [modulesLoading, setModulesLoading] = useState(true);
  const [modulesError, setModulesError] = useState(null);

  // Fetch user's accessible modules
  const fetchUserModules = async () => {
    if (!institution?.id || !currentUser?.email) return;

    try {
      setModulesLoading(true);
      setModulesError(null);

      // Get user's module access from colleges/{institution}/users/{email}
      const userDocRef = doc(db, 'colleges', institution.id, 'users', currentUser.email);
      const userDoc = await getDoc(userDocRef);

      if (!userDoc.exists()) {
        setUserModules([]);
        return;
      }

      const userData = userDoc.data();
      const userModuleIds = userData.modules || [];

      if (userModuleIds.length === 0) {
        setUserModules([]);
        return;
      }

      // Fetch the module configs for each module the user has access to
      const modulePromises = userModuleIds.map(async (moduleId) => {
        try {
          const moduleConfigRef = doc(db, 'colleges', institution.id, 'configs', moduleId);
          const moduleConfigDoc = await getDoc(moduleConfigRef);

          if (moduleConfigDoc.exists()) {
            const configData = moduleConfigDoc.data();
            return {
              id: moduleConfigDoc.id,
              moduleId: configData.moduleId || moduleConfigDoc.id,
              name: configData.name || 'Untitled Module',
              description: configData.description || 'No description available',
              iconName: configData.iconName,
              status: configData.status,
              enabledAt: configData.enabledAt,
              userRole: configData.userRole || 'member',
              ...configData
            };
          }
          return null;
        } catch (error) {
          console.error(`Error fetching config for module ${moduleId}:`, error);
          return null;
        }
      });

      const moduleConfigs = await Promise.all(modulePromises);

      // Filter out null results and sort by name
      const validModules = moduleConfigs
        .filter(module => module !== null)
        .sort((a, b) => a.name.localeCompare(b.name));

      setUserModules(validModules);
    } catch (err) {
      console.error('Error fetching user modules:', err);
      setModulesError('Failed to load your modules. Please try again.');
    } finally {
      setModulesLoading(false);
    }
  };

  // Fetch modules on component mount
  useEffect(() => {
    fetchUserModules();
  }, [institution?.id, currentUser?.email]);

  // Retry function for error state
  const retryFetchModules = () => {
    fetchUserModules();
  };

  // Handle module click navigation
  const handleModuleClick = (module) => {
    navigate(`${module.id}`);
  };

  return (
    <div className="h-full w-full text-white px-8 pt-8 flex flex-col overflow-x-clip overflow-y-auto">
      {/* Header */}
      <div className="w-full h-fit border-b border-[#ffffff10] pb-4 flex justify-between">
        <div className="text-3xl font-medium">Your Modules</div>
      </div>

      {/* Content */}
      <div className={`${userModules.length === 0 ? "flex-1" : "h-fit"} w-full pt-4 flex`}>
        {modulesLoading ? (
          /* Loading State */
          <div className="grid grid-cols-1 space-y-56 md:space-y-0 md:grid-cols-2 lg:grid-cols-3 gap-4 w-full">
            {[1, 2, 3, 4, 5, 6].map(placeholder => (
              <div className="w-full h-fit relative pt-4" key={placeholder}>
                <div className="p-4 w-full h-48 -translate-y-4 bg-[#1e1e1e] border border-[#ffffff10] rounded-xl flex flex-col justify-between animate-pulse">
                  <div className="flex space-x-2 items-center h-fit">
                    <div className="p-3 h-fit w-fit bg-[#ffffff20] rounded-lg">
                      <div className="w-6 h-6 bg-[#ffffff30] rounded"></div>
                    </div>
                    <div className="flex-1">
                      <div className="h-5 bg-[#ffffff20] rounded mb-2"></div>
                      <div className="h-4 bg-[#ffffff10] rounded w-3/4"></div>
                    </div>
                  </div>
                  <div className="flex space-x-2 items-center h-fit">
                    <div className="p-3 h-fit w-fit">
                      <div className="w-5 h-5 bg-[#ffffff20] rounded-full"></div>
                    </div>
                    <div className="flex-1">
                      <div className="h-4 bg-[#ffffff10] rounded w-1/2"></div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : modulesError ? (
          /* Error State */
          <div className="flex flex-col items-center w-full justify-center my-4 bg-[#ffffff10] border border-[#ffffff15] rounded-xl">
            <LuHardDrive className="scale-[300%] opacity-50 mb-4 text-red-400" />
            <div className="text-4xl mb-4 mt-2">Error Loading Modules</div>
            <div className="text-lg text-gray-400 mb-6">{modulesError}</div>
            <div
              onClick={retryFetchModules}
              className="px-4 py-1.5 font-medium border border-[#ffffff15] space-x-2 rounded-lg flex items-center justify-center hover:bg-[#ffffff20] transition cursor-pointer active:scale-95 transform duration-150 ease-out"
            >
              <div>Try Again</div>
            </div>
          </div>
        ) : userModules.length === 0 ? (
          /* No Modules State */
          <div className="flex flex-col items-center w-full justify-center my-4 bg-[#ffffff10] border border-[#ffffff15] rounded-xl">
            <LuHardDrive className="scale-[300%] opacity-50 mb-4" />
            <div className="text-4xl mb-4 mt-2">No Modules Available</div>
            <div className="text-lg text-gray-400 mb-6">
              You haven't been assigned to any modules yet.
            </div>
            <div className="text-sm text-gray-500">
              Contact your administrator to get access to modules.
            </div>
          </div>
        ) : (
          /* Modules Grid */
          <div className="grid grid-cols-1 space-y-56 md:space-y-0 md:grid-cols-2 lg:grid-cols-3 gap-4 w-full">
            {userModules.map(module => {
              const IconComponent = LucideIcons[module.iconName || 'LuPackage'] || LucideIcons.LuPackage;
              return (
                <div className="w-full h-fit relative pt-4" key={module.id}>
                  {/* Main Module Card */}
                  <div
                    onClick={() => handleModuleClick(module)}
                    className="p-4 hover:bg-[#2e2e2e] cursor-pointer transition w-full absolute z-10 h-48 -translate-y-4 bg-[#1e1e1e] border border-[#ffffff10] rounded-xl flex flex-col justify-between group"
                  >
                    {/* Module Header */}
                    <div className="flex space-x-2 items-center h-fit">
                      <div className="p-3 h-fit w-fit bg-[#ffffff10] rounded-lg group-hover:bg-[#ffffff15] transition-colors">
                        <IconComponent className="text-white w-6 h-6" />
                      </div>
                      <div>
                        <div className="text-lg font-medium text-white">{module.name}</div>
                        <div className="text-sm text-[#ffffff50]">{module.description}</div>
                      </div>
                    </div>

                    {/* Module Status */}
                    <div className="flex space-x-2 items-center h-fit">
                      <div className="p-3 h-fit w-fit">
                        <LuCheckCircle className="text-green-700 w-5 h-5" />
                      </div>
                      <div>
                        <div className="flex-1 h-fit text-white opacity-50 text-sm font-normal">
                          {module.status === 'Enabled' ? 'Available since ' : 'Enabled on '}
                          {module?.enabledAt?.toDate ?
                            module.enabledAt.toDate().toLocaleDateString('en-GB', {
                              day: '2-digit',
                              month: 'short',
                              year: 'numeric',
                            }) :
                            'Unknown'
                          }
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Module Footer/Status Bar */}
                  <div className="overflow-clip w-full absolute top-40 z-0 h-24 -translate-y-4 bg-[#1a1a1a] border-x border-b border-[#ffffff10] rounded-b-xl flex items-center space-x-2">
                    <div className="w-4 h-full bg-gradient-to-r from-[#ffffff10] to-transparent" />
                    <div className="flex-1 h-full flex items-end text-white opacity-50 text-sm font-medium">
                      <div className="h-[2.875rem] w-full flex items-center p-1">
                        <div className="h-full px-2 w-full rounded-lg flex items-center justify-between">
                          <div className="flex items-center justify-between w-full">
                            <div className="relative w-4 h-full flex items-center">
                              {module.status !== "Not Configured" ? (
                                <>
                                  <div className={"h-2 w-2 absolute bg-green-400 rounded-full animate-pulse"}/>
                                  <div className={"h-2 w-2 absolute bg-green-400 rounded-full"}/>
                                </>
                              ) : (
                                <>
                                  <div className={"h-2 w-2 absolute bg-orange-400 rounded-full animate-ping"}/>
                                  <div className={"h-2 w-2 absolute bg-orange-400 rounded-full"}/>
                                </>
                              )}
                            </div>
                            <div className="text-sm">
                              {module.userRole ? `Access as ${module.userRole}` : 'Member Access'}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="w-4 h-full bg-gradient-to-l from-[#ffffff10] to-transparent" />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default FacultyDashboard;